import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * Custom TranslateLoader that loads and merges multiple translation files.
 * Loads both the base language file (e.g., de.json) and an optional
 * custom file (e.g., de_custom.json) and merges them together.
 *
 * The custom file overwrites values from the base file.
 */
export class CustomTranslateLoader implements TranslateLoader {
  constructor(
    private http: HttpClient,
    private prefix: string = './assets/i18n/',
    private suffix: string = '.json',
  ) {}

  /**
   * Loads the translations for a specific language
   * @param lang Language code (e.g., 'de', 'en')
   */
  getTranslation(lang: string): Observable<any> {
    const baseFile = `${this.prefix}${lang}${this.suffix}`;
    const customFile = `${this.prefix}${lang}_custom${this.suffix}`;

    // Load both files in parallel
    return forkJoin({
      base: this.http.get(baseFile),
      custom: this.http.get(customFile).pipe(
        // If the custom file does not exist, return an empty object
        catchError(() => {
          console.info(`CustomTranslateLoader: No custom translations found for '${lang}'`);
          return of({});
        }),
      ),
    }).pipe(
      // Merge the two objects (custom overwrites base)
      map((result) => this.deepMerge(result.base, result.custom)),
    );
  }

  /**
   * Merges two objects recursively (deep merge)
   * @param target Base object
   * @param source Object to be overlaid
   */
  private deepMerge(target: any, source: any): any {
    // If source is empty, return target
    if (!source || Object.keys(source).length === 0) {
      return target;
    }

    const result = { ...target };

    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        // If both values are objects, merge recursively
        if (
          source[key] &&
          typeof source[key] === 'object' &&
          !Array.isArray(source[key]) &&
          target[key] &&
          typeof target[key] === 'object' &&
          !Array.isArray(target[key])
        ) {
          result[key] = this.deepMerge(target[key], source[key]);
        } else {
          // Otherwise overwrite the value
          result[key] = source[key];
        }
      }
    }

    return result;
  }
}
